import { Checkbox, NumberInput, Select, Textarea, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { Controller } from 'react-hook-form';
import type { FieldDescriptor, FieldRegistry, LayoutDocument } from './types';
import type { WellInputSchema } from '../schemas';
import type { LandLookups } from '../../types/api';
import { CreatableLookupSelect } from './lease-lookup-select';

/**
 * Well edit-form field registry. Mirrors
 * api/src/lib/well-form-layout-schema.ts — the field-name list below MUST
 * stay in sync with that file's `WELL_FORM_FIELD_NAMES` (73 fields).
 *
 * Mechanical clone of contract-land-fields.tsx / lease-fields.tsx: same
 * context-bag shape (lookups / members / canAssignAnyReviewer /
 * currentUserId), same reviewer field behavior, same helper-based
 * descriptor builders. Wells additionally needs `numberField` (from
 * lease-fields.tsx) for the 16-number interest matrix + unit_acreage +
 * production counts.
 *
 * Context bag carries the seeded dropdown options (`useLandLookups()`,
 * `GET /land-data/lookups`) — fetched once by the caller (the well form
 * drawer / layout editor preview) and threaded down. Fields never call the
 * query hook themselves: `desc.render` is invoked inline inside a `.map()`
 * in `FormLayoutRenderer`, which would break the Rules of Hooks.
 */

export const WELL_FORM_FIELD_NAMES = [
  'property_number',
  'property_name',
  'api',
  'alt_prop_number',
  'prospect',
  'field',
  'rsrv_cat',
  'abstract',
  'survey',
  'roll_up',
  'reviewer_user_id',

  'well_status',
  'well_type',
  'unit_type',
  'fed_rights',
  'unit_acreage',
  'do_number',
  'owner',
  'operator',

  'county',
  'state',
  'township',
  'range',
  'section',
  'block',

  'description',
  'flag_for_issues',
  'exception_remark',
  'title_issues',

  'oil_bpo_wi',
  'oil_apo_wi',
  'oil_bpo_nri',
  'oil_apo_nri',
  'oil_bpo_orri',
  'oil_apo_orri',
  'oil_bpo_ri',
  'oil_apo_ri',
  'gas_bpo_wi',
  'gas_apo_wi',
  'gas_bpo_nri',
  'gas_apo_nri',
  'gas_bpo_orri',
  'gas_apo_orri',
  'gas_bpo_ri',
  'gas_apo_ri',

  'minerals',
  'royalties',
  'participating',
  'non_participating',
  'term',
  'perpetual',
  'call_on_prod',
  'non_consent',
  'non_consent_penalty',
  'payout',
  'payout_amount',
  'payout_date',

  'first_prod',
  'last_prod',
  'oil_data_as_of',
  'oil_cum',
  'oil_avg_bpm_6mo',
  'oil_avg_bopd_6mo',
  'gas_data_as_of',
  'gas_cum',
  'gas_avg_mcf_6mo',
  'gas_avg_mcfd_6mo',
  'num_completions',
  'total_depth',
  'prod_zones',

  'remarks',
  'reviewer_notes',
  'change_remarks',
] as const;

export type WellFormFieldName = (typeof WELL_FORM_FIELD_NAMES)[number];

export interface WellFormMemberOption {
  value: string;
  label: string;
}

export interface WellFormContext {
  lookups: LandLookups;
  /** Current project's members, for the reviewer_user_id dropdown. */
  members: WellFormMemberOption[];
  /** owner/superadmin may assign any member; moderator/editor are self-only. */
  canAssignAnyReviewer: boolean;
  currentUserId: string;
}

type WellFieldDescriptor = FieldDescriptor<WellInputSchema, WellFormContext>;

function textProps(
  descriptor: { label: string; placeholder?: string },
  disabled: boolean,
  required = false,
) {
  const props: { label: string; placeholder?: string; disabled?: boolean; required?: boolean } = {
    label: descriptor.label,
  };
  if (descriptor.placeholder !== undefined) props.placeholder = descriptor.placeholder;
  if (disabled) props.disabled = true;
  if (required) props.required = true;
  return props;
}

function textField(
  label: string,
  defaultSpan: number,
  opts: { placeholder?: string; required?: boolean } = {},
): WellFieldDescriptor {
  const desc: WellFieldDescriptor = {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <TextInput {...textProps(descriptor, disabled, opts.required)} {...register(name)} />
    ),
  };
  if (opts.placeholder !== undefined) desc.placeholder = opts.placeholder;
  return desc;
}

function textareaField(label: string, defaultSpan: number): WellFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <Textarea autosize minRows={2} {...textProps(descriptor, disabled)} {...register(name)} />
    ),
  };
}

function dateField(label: string, defaultSpan: number): WellFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <DateInput
            label={descriptor.label}
            valueFormat="YYYY-MM-DD"
            value={(field.value as string | null) ?? null}
            onChange={(v) => field.onChange(v ?? null)}
            clearable
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

/**
 * NumberInput field. The 16-number interest matrix + unit_acreage are
 * NUMERIC(12,10)/(12,2) columns holding fractional ownership interests
 * (e.g. 0.0002448) — `decimalScale`/`step` default to 10-decimal precision
 * so those round-trip exactly; production counts pass `decimalScale: 0` for
 * plain integers.
 */
function numberField(
  label: string,
  defaultSpan: number,
  opts: { decimalScale?: number; step?: number } = {},
): WellFieldDescriptor {
  const decimalScale = opts.decimalScale ?? 10;
  const step = opts.step ?? (decimalScale > 0 ? 10 ** -decimalScale : 1);
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <NumberInput
            label={descriptor.label}
            decimalScale={decimalScale}
            step={step}
            min={0}
            value={(field.value as number | null) ?? ''}
            onChange={(v) => field.onChange(typeof v === 'number' ? v : null)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

function checkboxField(label: string, defaultSpan: number): WellFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Checkbox
            label={descriptor.label}
            checked={(field.value as boolean | null) ?? false}
            onChange={(e) => field.onChange(e.currentTarget.checked)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

function lookupSelectField(
  label: string,
  kind: keyof LandLookups,
  defaultSpan: number,
): WellFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, context, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <CreatableLookupSelect
            label={descriptor.label}
            options={context.lookups[kind]}
            value={(field.value as string | null) ?? null}
            onChange={(v) => field.onChange(v)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

/**
 * Reviewer dropdown: a Select of the current project's members. Behavior is
 * identical to lease-fields.tsx / contract-land-fields.tsx's `reviewerField`
 * (the change-only authz lives server-side in api/src/routes/wells.ts's
 * assertReviewerAssignable) — mirrored, not reimplemented from scratch. For
 * moderator/editor callers (`context.canAssignAnyReviewer === false`) the
 * option list is restricted to self-only, but the CURRENT value must still
 * be visible and round-trip unchanged: if it belongs to someone else the
 * Select renders disabled (visible, not reassignable); if it's already self
 * (or empty) the normal self-only picker applies.
 */
const reviewerField: WellFieldDescriptor = {
  label: 'Reviewer',
  defaultSpan: 4,
  render: ({ name, control, disabled, context }) => {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const currentValue = (field.value as string | null) ?? null;
          const isOtherReviewer =
            !context.canAssignAnyReviewer &&
            currentValue !== null &&
            currentValue !== context.currentUserId;
          const options = context.canAssignAnyReviewer
            ? context.members
            : context.members.filter((m) => m.value === context.currentUserId);
          if (isOtherReviewer && !options.some((m) => m.value === currentValue)) {
            const known = context.members.find((m) => m.value === currentValue);
            options.push({ value: currentValue, label: known?.label ?? currentValue });
          }
          return (
            <Select
              label="Reviewer"
              data={options}
              searchable
              clearable
              value={currentValue}
              onChange={(v) => field.onChange(v ?? null)}
              disabled={disabled || isOtherReviewer}
            />
          );
        }}
      />
    );
  },
};

export const WELL_FORM_FIELDS: FieldRegistry<WellInputSchema, WellFormFieldName, WellFormContext> = {
  property_number: textField('Property #', 4, { required: true }),
  property_name: textField('Property name', 4),
  api: textField('API', 4),
  alt_prop_number: textField('Alt property #', 4),
  prospect: textField('Prospect', 4),
  field: textField('Field', 4),
  rsrv_cat: textField('Reserve category', 4),
  abstract: textField('Abstract', 4),
  survey: textField('Survey', 4),
  roll_up: checkboxField('Roll up', 3),
  reviewer_user_id: reviewerField,

  well_status: lookupSelectField('Status', 'well_status', 4),
  well_type: lookupSelectField('Type', 'well_type', 4),
  unit_type: lookupSelectField('Unit type', 'unit_type', 4),
  fed_rights: lookupSelectField('Federal rights', 'fed_rights', 4),
  unit_acreage: numberField('Unit acreage', 4, { decimalScale: 2 }),
  do_number: textField('DO #', 4),
  owner: textField('Owner', 4),
  operator: textField('Operator', 4),

  county: textField('County', 6),
  state: textField('State', 6),
  township: textField('Township', 3),
  range: textField('Range', 3),
  section: textField('Section', 3),
  block: textField('Block', 3),

  description: textareaField('Description', 12),
  flag_for_issues: lookupSelectField('Flag for issues', 'flag_for_issues', 4),
  exception_remark: textareaField('Exception remark', 8),
  title_issues: textareaField('Title issues', 12),

  oil_bpo_wi: numberField('Oil BPO WI', 3),
  oil_apo_wi: numberField('Oil APO WI', 3),
  oil_bpo_nri: numberField('Oil BPO NRI', 3),
  oil_apo_nri: numberField('Oil APO NRI', 3),
  oil_bpo_orri: numberField('Oil BPO ORRI', 3),
  oil_apo_orri: numberField('Oil APO ORRI', 3),
  oil_bpo_ri: numberField('Oil BPO RI', 3),
  oil_apo_ri: numberField('Oil APO RI', 3),
  gas_bpo_wi: numberField('Gas BPO WI', 3),
  gas_apo_wi: numberField('Gas APO WI', 3),
  gas_bpo_nri: numberField('Gas BPO NRI', 3),
  gas_apo_nri: numberField('Gas APO NRI', 3),
  gas_bpo_orri: numberField('Gas BPO ORRI', 3),
  gas_apo_orri: numberField('Gas APO ORRI', 3),
  gas_bpo_ri: numberField('Gas BPO RI', 3),
  gas_apo_ri: numberField('Gas APO RI', 3),

  minerals: checkboxField('Minerals', 3),
  royalties: checkboxField('Royalties', 3),
  participating: checkboxField('Participating', 3),
  non_participating: checkboxField('Non-participating', 3),
  term: checkboxField('Term', 3),
  perpetual: checkboxField('Perpetual', 3),
  call_on_prod: checkboxField('Call on production', 3),
  non_consent: checkboxField('Non-consent', 3),
  non_consent_penalty: textField('Non-consent penalty', 6),
  payout: checkboxField('Payout', 3),
  payout_amount: textField('Payout amount', 6),
  payout_date: dateField('Payout date', 3),

  first_prod: dateField('First production', 3),
  last_prod: dateField('Last production', 3),
  oil_data_as_of: dateField('Oil reported as of', 3),
  oil_cum: numberField('Oil cum', 3, { decimalScale: 0 }),
  oil_avg_bpm_6mo: numberField('Oil avg BPM (6mo)', 3, { decimalScale: 0 }),
  oil_avg_bopd_6mo: numberField('Oil avg BOPD (6mo)', 3, { decimalScale: 0 }),
  gas_data_as_of: dateField('Gas reported as of', 3),
  gas_cum: numberField('Gas cum', 3, { decimalScale: 0 }),
  gas_avg_mcf_6mo: numberField('Gas avg MCF (6mo)', 3, { decimalScale: 0 }),
  gas_avg_mcfd_6mo: numberField('Gas avg MCFD (6mo)', 3, { decimalScale: 0 }),
  num_completions: numberField('Num completions', 3, { decimalScale: 0 }),
  total_depth: numberField('Total depth', 3, { decimalScale: 0 }),
  prod_zones: textField('Production zones', 6),

  remarks: textareaField('Remarks', 12),
  reviewer_notes: textareaField('Reviewer notes', 12),
  change_remarks: textareaField('Change remarks', 12),
};

/**
 * Built-in default. Groups mirror
 * api/src/lib/well-form-layout-schema.ts's `DEFAULT_LAYOUT` (identity /
 * classification / location+PLSS / description+review / oil interest / gas
 * interest / ownership flags+payout / production info / narrative) so a
 * freshly-deployed DB renders sensibly before any admin save.
 */
export const WELL_DEFAULT_LAYOUT: LayoutDocument<WellFormFieldName> = {
  version: 1,
  groups: [
    {
      id: 'identity',
      title: 'Identity',
      fields: [
        { name: 'property_number', span: 4 },
        { name: 'property_name', span: 4 },
        { name: 'api', span: 4 },
        { name: 'alt_prop_number', span: 4 },
        { name: 'prospect', span: 4 },
        { name: 'field', span: 4 },
        { name: 'rsrv_cat', span: 4 },
        { name: 'abstract', span: 4 },
        { name: 'survey', span: 4 },
        { name: 'roll_up', span: 3 },
        { name: 'reviewer_user_id', span: 4 },
      ],
    },
    {
      id: 'classification',
      title: 'Classification',
      fields: [
        { name: 'well_status', span: 4 },
        { name: 'well_type', span: 4 },
        { name: 'unit_type', span: 4 },
        { name: 'fed_rights', span: 4 },
        { name: 'unit_acreage', span: 4 },
        { name: 'do_number', span: 4 },
        { name: 'owner', span: 4 },
        { name: 'operator', span: 4 },
      ],
    },
    {
      id: 'plss_location',
      title: 'Location / PLSS',
      fields: [
        { name: 'county', span: 6 },
        { name: 'state', span: 6 },
        { name: 'township', span: 3 },
        { name: 'range', span: 3 },
        { name: 'section', span: 3 },
        { name: 'block', span: 3 },
      ],
    },
    {
      id: 'description_review',
      title: 'Description / review',
      fields: [
        { name: 'description', span: 12 },
        { name: 'flag_for_issues', span: 4 },
        { name: 'exception_remark', span: 8 },
        { name: 'title_issues', span: 12 },
      ],
    },
    {
      id: 'oil_interest',
      title: 'Oil Interest',
      fields: [
        { name: 'oil_bpo_wi', span: 3 },
        { name: 'oil_apo_wi', span: 3 },
        { name: 'oil_bpo_nri', span: 3 },
        { name: 'oil_apo_nri', span: 3 },
        { name: 'oil_bpo_orri', span: 3 },
        { name: 'oil_apo_orri', span: 3 },
        { name: 'oil_bpo_ri', span: 3 },
        { name: 'oil_apo_ri', span: 3 },
      ],
    },
    {
      id: 'gas_interest',
      title: 'Gas Interest',
      fields: [
        { name: 'gas_bpo_wi', span: 3 },
        { name: 'gas_apo_wi', span: 3 },
        { name: 'gas_bpo_nri', span: 3 },
        { name: 'gas_apo_nri', span: 3 },
        { name: 'gas_bpo_orri', span: 3 },
        { name: 'gas_apo_orri', span: 3 },
        { name: 'gas_bpo_ri', span: 3 },
        { name: 'gas_apo_ri', span: 3 },
      ],
    },
    {
      id: 'ownership_payout',
      title: 'Ownership flags / payout',
      fields: [
        { name: 'minerals', span: 3 },
        { name: 'royalties', span: 3 },
        { name: 'participating', span: 3 },
        { name: 'non_participating', span: 3 },
        { name: 'term', span: 3 },
        { name: 'perpetual', span: 3 },
        { name: 'call_on_prod', span: 3 },
        { name: 'non_consent', span: 3 },
        { name: 'non_consent_penalty', span: 6 },
        { name: 'payout', span: 3 },
        { name: 'payout_amount', span: 6 },
        { name: 'payout_date', span: 3 },
      ],
    },
    {
      id: 'production_info',
      title: 'Production Info',
      fields: [
        { name: 'first_prod', span: 3 },
        { name: 'last_prod', span: 3 },
        { name: 'oil_data_as_of', span: 3 },
        { name: 'oil_cum', span: 3 },
        { name: 'oil_avg_bpm_6mo', span: 3 },
        { name: 'oil_avg_bopd_6mo', span: 3 },
        { name: 'gas_data_as_of', span: 3 },
        { name: 'gas_cum', span: 3 },
        { name: 'gas_avg_mcf_6mo', span: 3 },
        { name: 'gas_avg_mcfd_6mo', span: 3 },
        { name: 'num_completions', span: 3 },
        { name: 'total_depth', span: 3 },
        { name: 'prod_zones', span: 6 },
      ],
    },
    {
      id: 'narrative',
      title: 'Narrative',
      fields: [
        { name: 'remarks', span: 12 },
        { name: 'reviewer_notes', span: 12 },
        { name: 'change_remarks', span: 12 },
      ],
    },
  ],
};
